import React, { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/useAuthStore";
import {
  isBeverageOrBeveragePsdRecipe,
  isConfectionaryRecipe,
} from "../../application-recipes/data/ingredientsCalculations";
import {
  mapUIParamsToBackend,
  mergeProcedureParamsWithTemplate,
  parseStoredComments,
} from "../../application-recipes/data/sopDataByFormat";

/**
 * Sample-scoped Standard Operating Procedure.
 *
 * Intentionally separate from the recipe
 * `application-recipes/components/StandardOperatingProcedure.jsx`. This one is
 * fully controlled by `value` / `onChange` and only ever writes to
 * `Sample.sop`, so a sample's SOP can be changed without touching the recipe.
 */

const EMPTY_TUNNEL_BAKING = { topTemperature: [], bottomTemperature: [], bakingTime: null, beltSpeed: null };
const EMPTY_NORMAL_BAKING = { ovenTemperature: [], ovenTime: [], steam: null, steam2: null };
const EMPTY_AFTER_BAKE = { weight: null, size: null, aeration: null, aW: null, moisture: null };

// `getProcedureParamsTemplate` has no bakery template, so fall back to the same
// defaults the recipe SOP uses for bakery when nothing has been copied yet.
const BAKERY_DEFAULT_PARAMS = [
  { label: "Dough Temperature", value: "", unit: "°C" },
  { label: "SG", value: "", unit: "" },
  { label: "pH", value: "", unit: "" },
];

const EMPTY_SOP = {
  procedureSOP: "",
  procedureParameters: [],
  ovenTemperatureTunnelBaking: EMPTY_TUNNEL_BAKING,
  normalBaking: EMPTY_NORMAL_BAKING,
  afterBake: EMPTY_AFTER_BAKE,
  procedureOthers: "",
  procedureSensoryFeedback: "",
};

function SectionCard({ title, badge, children, className = "" }) {
  return (
    <div
      className={cn(
        "p-3.5 rounded-2xl bg-[#FAF8FD] dark:bg-[#181424] border border-[#EEEBF4] dark:border-primary/30 space-y-3",
        className
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-[#4B208B] dark:text-purple-300 uppercase tracking-wider">
          {title}
        </span>
        {badge && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function NumberField({ label, value, onChange, unit, disabled = false }) {
  return (
    <div>
      <span className="text-gray-400 text-[10px]">{label}</span>
      <div className="flex items-center gap-1.5">
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          disabled={disabled}
          className="w-full p-2 mt-1 rounded-lg bg-white dark:bg-[#121019] border border-gray-200 dark:border-primary/30 font-bold text-xs outline-none focus:border-primary disabled:opacity-60 disabled:cursor-not-allowed"
        />
        {unit && <span className="text-[10px] text-gray-400 shrink-0">{unit}</span>}
      </div>
    </div>
  );
}

function ArrayNumberField({ label, values = [], onChange, unit, disabled = false, count = 3 }) {
  const slots = Array.from({ length: count }, (_, i) => i);
  return (
    <div>
      <span className="text-gray-400 text-[10px]">{label}</span>
      <div className={cn("grid gap-1.5 mt-1", count === 2 ? "grid-cols-2" : "grid-cols-3")}>
        {slots.map((idx) => (
          <input
            key={idx}
            type="number"
            value={values?.[idx] ?? ""}
            onChange={(e) => {
              const next = slots.map((i) => {
                if (i < values.length) return values[i];
                return null;
              });
              next[idx] = e.target.value === "" ? null : Number(e.target.value);
              onChange(next.filter((v) => v !== null && v !== undefined));
            }}
            disabled={disabled}
            placeholder={`${idx + 1}`}
            className="w-full p-2 rounded-lg bg-white dark:bg-[#121019] border border-gray-200 dark:border-primary/30 font-bold text-xs text-center outline-none focus:border-primary disabled:opacity-60 disabled:cursor-not-allowed"
          />
        ))}
      </div>
      {unit && <span className="text-[10px] text-gray-400">{unit}</span>}
    </div>
  );
}

export default function SampleStandardOperatingProcedure({
  value,
  onChange,
  recipeFormat = "bakery",
  disabled = false,
  isFromFinalRecipe = false,
  showComments = true,
}) {
  const { user: currentUser } = useAuthStore();
  const currentUserName =
    currentUser?.name || currentUser?.fullName || currentUser?.username || "User";
  const [newCommentText, setNewCommentText] = useState("");

  const sop = { ...EMPTY_SOP, ...(value || {}) };
  const tunnelBaking = { ...EMPTY_TUNNEL_BAKING, ...(sop.ovenTemperatureTunnelBaking || {}) };
  const normalBaking = { ...EMPTY_NORMAL_BAKING, ...(sop.normalBaking || {}) };
  const afterBake = { ...EMPTY_AFTER_BAKE, ...(sop.afterBake || {}) };

  const shouldShowBakeSections =
    !isConfectionaryRecipe(recipeFormat) &&
    !isBeverageOrBeveragePsdRecipe(recipeFormat);
  const isBakery = shouldShowBakeSections;

  const canEditProcedure = false;
  const canEditParameters = !disabled && (isFromFinalRecipe ? !isBakery : true);
  const canEditOven = !disabled && !isFromFinalRecipe;
  const canEditAfterBake = !disabled;

  const uiParams = useMemo(() => {
    const merged = mergeProcedureParamsWithTemplate(sop.procedureParameters || [], recipeFormat);
    if (merged.length > 0) return merged;
    return String(recipeFormat || "").toLowerCase().includes("bakery") ? BAKERY_DEFAULT_PARAMS : [];
  }, [sop.procedureParameters, recipeFormat]);

  const comments = useMemo(
    () => parseStoredComments(sop.procedureOthers, currentUserName),
    [sop.procedureOthers, currentUserName]
  );

  const patch = (fields) => onChange?.({ ...sop, ...fields });

  const setParamValue = (idx, nextValue) => {
    const next = uiParams.map((p, i) => (i === idx ? { ...p, value: nextValue } : p));
    patch({ procedureParameters: mapUIParamsToBackend(next) });
  };

  const addComment = () => {
    const text = newCommentText.trim();
    if (!text) return;
    const entry = {
      userName: currentUserName,
      user: currentUserName,
      name: currentUserName,
      text,
      comment: text,
      createdAt: new Date().toISOString(),
    };
    patch({ procedureOthers: JSON.stringify([...comments, entry]) });
    setNewCommentText("");
  };

  const updateComment = (idx, nextText) => {
    const next = comments.map((c, i) =>
      i === idx ? { ...c, text: nextText, comment: nextText } : c
    );
    patch({ procedureOthers: JSON.stringify(next) });
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 rounded-xl text-xs text-emerald-700 dark:text-emerald-300">
        This SOP was copied from the application recipe when this sample was created.
        Editing it changes this sample only &mdash; the recipe stays untouched.
      </div>

      {/* Procedure */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">
            Procedure (SOP)
          </label>
          <span className={cn(
            "text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider",
            canEditProcedure
              ? "bg-purple-100 dark:bg-primary/20 text-[#4B208B] dark:text-purple-300"
              : "bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
          )}>
            {canEditProcedure ? "✏️ Sample Copy" : "🔒 Read Only"}
          </span>
        </div>
        <textarea
          rows={5}
          value={sop.procedureSOP || ""}
          onChange={(e) => patch({ procedureSOP: e.target.value })}
          disabled={!canEditProcedure}
          placeholder="No procedure steps specified in recipe."
          className="w-full p-3.5 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] border border-transparent focus:border-primary outline-none font-medium text-xs text-gray-900 dark:text-white resize-none custom-scrollbar disabled:opacity-60"
        />
      </div>

      {/* Parameters */}
      {uiParams.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">
              Parameters
            </label>
            <span className={cn(
              "text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider",
              canEditParameters
                ? "bg-purple-100 dark:bg-primary/20 text-[#4B208B] dark:text-purple-300"
                : "bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
            )}>
              {canEditParameters ? "✏️ Sample Copy" : "🔒 Read Only"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {uiParams.map((param, pIdx) => (
              <div
                key={pIdx}
                className="flex items-center gap-2 p-2 rounded-xl bg-white dark:bg-[#121019] border border-gray-200 dark:border-primary/30"
              >
                <span className="text-xs font-bold text-gray-700 dark:text-gray-300 flex-1 truncate">
                  {param.label}
                </span>
                <input
                  type="text"
                  value={param.value ?? ""}
                  onChange={(e) => setParamValue(pIdx, e.target.value)}
                  disabled={!canEditParameters}
                  placeholder="Value"
                  className="w-24 p-1.5 rounded-lg bg-gray-50 dark:bg-[#1D1829] border border-gray-200 dark:border-primary/30 font-bold text-xs outline-none focus:border-primary disabled:opacity-60"
                />
                {param.unit && (
                  <span className="text-[10px] font-semibold text-gray-400">{param.unit}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Oven Temperature (Bakery only) */}
      {shouldShowBakeSections && (
        <SectionCard
          title="Baking & Oven Parameters"
          badge={canEditOven ? "✏️ Sample Copy" : "🔒 Read Only"}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ArrayNumberField
              label="Tunnel Top Temperature (°C)"
              values={tunnelBaking.topTemperature}
              onChange={(next) => patch({ ovenTemperatureTunnelBaking: { ...tunnelBaking, topTemperature: next } })}
              disabled={!canEditOven}
            />
            <ArrayNumberField
              label="Tunnel Bottom Temperature (°C)"
              values={tunnelBaking.bottomTemperature}
              onChange={(next) => patch({ ovenTemperatureTunnelBaking: { ...tunnelBaking, bottomTemperature: next } })}
              disabled={!canEditOven}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <NumberField
              label="Baking Time (min)"
              value={tunnelBaking.bakingTime}
              onChange={(next) => patch({ ovenTemperatureTunnelBaking: { ...tunnelBaking, bakingTime: next } })}
              disabled={!canEditOven}
            />
            <NumberField
              label="Belt Speed (min)"
              value={tunnelBaking.beltSpeed}
              onChange={(next) => patch({ ovenTemperatureTunnelBaking: { ...tunnelBaking, beltSpeed: next } })}
              disabled={!canEditOven}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ArrayNumberField
              label="Rotary Temperature (°C)"
              count={2}
              values={normalBaking.ovenTemperature}
              onChange={(next) => patch({ normalBaking: { ...normalBaking, ovenTemperature: next } })}
              disabled={!canEditOven}
            />
            <ArrayNumberField
              label="Oven Time (min)"
              count={2}
              values={normalBaking.ovenTime}
              onChange={(next) => patch({ normalBaking: { ...normalBaking, ovenTime: next } })}
              disabled={!canEditOven}
            />
            <div>
              <span className="text-gray-400 text-[10px]">Steam (units)</span>
              <div className="grid grid-cols-2 gap-1.5">
                <NumberField
                  value={normalBaking.steam}
                  onChange={(next) => patch({ normalBaking: { ...normalBaking, steam: next } })}
                  disabled={!canEditOven}
                />
                <NumberField
                  value={normalBaking.steam2}
                  onChange={(next) => patch({ normalBaking: { ...normalBaking, steam2: next } })}
                  disabled={!canEditOven}
                />
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {shouldShowBakeSections && (
        <SectionCard
          title="After-Bake Analysis"
          badge={canEditAfterBake ? "✏️ Sample Copy" : "🔒 Read Only"}
        >
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <NumberField
              label="Weight (g)"
              value={afterBake.weight}
              onChange={(next) => patch({ afterBake: { ...afterBake, weight: next } })}
              disabled={!canEditAfterBake}
            />
            <NumberField
              label="Size"
              value={afterBake.size}
              onChange={(next) => patch({ afterBake: { ...afterBake, size: next } })}
              disabled={!canEditAfterBake}
            />
            <NumberField
              label="Aeration"
              value={afterBake.aeration}
              onChange={(next) => patch({ afterBake: { ...afterBake, aeration: next } })}
              disabled={!canEditAfterBake}
            />
            <NumberField
              label="aW"
              value={afterBake.aW}
              onChange={(next) => patch({ afterBake: { ...afterBake, aW: next } })}
              disabled={!canEditAfterBake}
            />
            <NumberField
              label="Moisture"
              value={afterBake.moisture}
              onChange={(next) => patch({ afterBake: { ...afterBake, moisture: next } })}
              disabled={!canEditAfterBake}
            />
          </div>
        </SectionCard>
      )}

      {/* Sensory Feedback */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">
            Procedure Sensory Feedback
          </label>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider bg-purple-100 dark:bg-primary/20 text-[#4B208B] dark:text-purple-300">
            ✏️ Sample Only
          </span>
        </div>
        <textarea
          rows={2}
          value={sop.procedureSensoryFeedback || ""}
          onChange={(e) => patch({ procedureSensoryFeedback: e.target.value })}
          disabled={disabled}
          placeholder="Record sensory feedback, taste notes, or visual inspection from this sample run..."
          className="w-full p-3 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] border border-transparent focus:border-primary outline-none font-medium text-xs text-gray-900 dark:text-white resize-none disabled:opacity-60"
        />
      </div>

      {showComments && (
        <>
          {/* Others (sample-scoped comments) */}
          <SectionCard title="Others" badge="✏️ Sample Only">
        {comments.length > 0 ? (
          <div className="space-y-2">
            {comments.map((c, idx) => (
              <div
                key={idx}
                className="p-3 rounded-xl bg-white dark:bg-[#121019] border border-gray-200 dark:border-primary/30 space-y-1.5"
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-primary text-white font-bold text-[10px] flex items-center justify-center">
                    {(c.userName || c.user || c.name || "U").slice(0, 2).toUpperCase()}
                  </div>
                  <span className="text-xs font-semibold text-[#0D111A] dark:text-white truncate">
                    {c.userName || c.user || c.name || "User"}
                  </span>
                  <span className="text-[10px] text-[#757575] ml-auto whitespace-nowrap">
                    {c.createdAt
                      ? new Date(c.createdAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "-"}
                  </span>
                </div>
                <textarea
                  rows={2}
                  value={c.text || c.comment || ""}
                  onChange={(e) => updateComment(idx, e.target.value)}
                  disabled={disabled}
                  className="w-full p-2 rounded-lg bg-gray-50 dark:bg-[#1D1829] border border-gray-200 dark:border-primary/30 font-medium text-xs text-gray-900 dark:text-white resize-none outline-none focus:border-primary disabled:opacity-60"
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">No comments recorded under Others.</p>
        )}

        <textarea
          rows={2}
          value={newCommentText}
          onChange={(e) => setNewCommentText(e.target.value)}
          disabled={disabled}
          placeholder="Add a comment about this sample run..."
          className="w-full p-2.5 rounded-lg bg-gray-50 dark:bg-[#1D1829] border border-gray-200 dark:border-primary/30 font-medium text-xs text-gray-900 dark:text-white resize-none outline-none focus:border-primary disabled:opacity-60"
        />

        <button
          type="button"
          onClick={addComment}
          disabled={disabled || !newCommentText.trim()}
          className="w-full py-2 rounded-xl border border-[#4B208B] text-[#4B208B] dark:text-purple-300 font-bold text-xs hover:bg-[#F7F5FA] dark:hover:bg-primary/10 transition-all cursor-pointer disabled:opacity-50"
        >
          Add Comment
        </button>
          </SectionCard>
        </>
      )}
    </div>
  );
}
